const PIXEL_ID = '24912583988382522';
const PILI_ENDPOINT = 'https://pili.migro.es/api/pili/ads/conversions/track';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

export type LeadEventName =
  | 'Lead'
  | 'CompleteRegistration'
  | 'TestCompleted'
  | 'LeadFormStarted'
  | 'ViewContent'
  | 'PageView';

export interface LeadFormData {
  email?: string | null;
  phone?: string | null;
  name?: string | null;
}

export interface CustomData {
  content_name?: string;
  content_category?: string;
  [key: string]: unknown;
}

const STANDARD_PIXEL_EVENTS = new Set<LeadEventName>([
  'Lead',
  'CompleteRegistration',
  'ViewContent',
  'PageView',
]);

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function splitName(fullName: string): { firstName: string | null; lastName: string | null } {
  const parts = fullName.trim().split(/\s+/);
  return {
    firstName: parts[0] || null,
    lastName: parts.length > 1 ? parts.slice(1).join(' ') : null,
  };
}

function genEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function sendToCapi(payload: Record<string, unknown>): void {
  try {
    fetch(PILI_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch((e) => console.error('[pili-track] CAPI error:', e));
  } catch (e) {
    console.error('[pili-track] CAPI throw:', e);
  }
}

/**
 * Dispara un evento simultáneamente en Meta Pixel (browser) y Meta CAPI (server)
 * con un mismo `event_id` para que Meta deduplique. Devuelve el event_id usado.
 *
 * Para custom events sin email/phone (TestCompleted con form vacío, LeadFormStarted),
 * el server matchea con fbc/fbp/IP/UA — sigue siendo válido, sólo con menor EMQ.
 */
export async function trackPixelAndCapi(
  eventName: LeadEventName,
  formData: LeadFormData = {},
  customData: CustomData = {},
): Promise<string> {
  const eventId = genEventId();
  const fbc = getCookie('_fbc');
  const fbp = getCookie('_fbp');
  const { firstName, lastName } = splitName(formData.name || '');

  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    const trackFn = STANDARD_PIXEL_EVENTS.has(eventName) ? 'track' : 'trackCustom';
    try {
      window.fbq(trackFn, eventName, customData, { eventID: eventId });
    } catch (e) {
      console.error('[pili-track] Pixel error:', e);
    }
  }

  sendToCapi({
    event_id: eventId,
    event_name: eventName,
    pixel_id: PIXEL_ID,
    email: formData.email || null,
    phone: formData.phone || null,
    first_name: firstName,
    last_name: lastName,
    country: 'es',
    fbc,
    fbp,
    event_source_url: window.location.href,
    client_user_agent: navigator.userAgent,
    custom_data: customData,
  });

  return eventId;
}

export async function trackConversion(formData: LeadFormData): Promise<string> {
  return trackPixelAndCapi('Lead', formData);
}

export async function trackTestCompleted(
  via: string,
  eligible: boolean,
  formData: LeadFormData = {},
): Promise<string> {
  return trackPixelAndCapi('TestCompleted', formData, {
    content_category: via,
    content_name: 'regularizacion_test',
    eligible,
  });
}

export async function trackLeadFormStarted(landingProduct: string): Promise<string> {
  return trackPixelAndCapi('LeadFormStarted', {}, {
    content_name: landingProduct,
  });
}

export async function trackViewContent(landingProduct: string): Promise<string> {
  return trackPixelAndCapi('ViewContent', {}, {
    content_name: landingProduct,
  });
}
